import {
  DepositoryType,
  useAccountPlaidJsonQuery,
  useBalanceReadingsQuery,
} from '@transactions/graphql';
import Money from 'common/Money';
import {Check, ChevronDown, ChevronRight, Copy, Trash2} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {toDisplayBalance} from 'src/account/balanceDisplay';
import {PlaidConnectionStatus} from 'src/account/PlaidConnectionStatus';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import {JsonTree} from 'src/JsonTree';
import {useModals} from 'src/Modals';
import type {BalanceReadingsQuery} from '@transactions/graphql';
import type {
  DepositoryAccount,
  InvestmentAccount,
} from 'src/account/AccountDetail';
import type {JsonValue} from 'src/JsonTree';

/** Narrow a parsed JSON value to a plain object. */
const asObject = (
  value: JsonValue | null | undefined,
): {[key: string]: JsonValue} | null =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;

const getString = (
  obj: {[key: string]: JsonValue} | null,
  key: string,
): string | null => {
  const value = obj?.[key];
  return typeof value === 'string' ? value : null;
};

const getNumber = (
  obj: {[key: string]: JsonValue} | null,
  key: string,
): number | null => {
  const value = obj?.[key];
  return typeof value === 'number' ? value : null;
};

/** Format a Plaid dollar amount (raw JSON stores dollars, not cents). */
const formatDollars = (
  dollars: number | null,
  currency: string,
): string | null =>
  dollars == null
    ? null
    : Money.toDecimalString(
        Money.fromServer({amount: Math.round(dollars * 100), currency}),
      );

const SummaryItem = ({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) => (
  <div className="min-w-0">
    <p className="text-muted-foreground text-xs uppercase tracking-wide">
      {label}
    </p>
    <p
      className="text-foreground mt-0.5 truncate text-sm"
      title={value ?? undefined}
    >
      {value ?? '—'}
    </p>
  </div>
);

type SectionProps = {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

/** A collapsible section inside the Plaid card. Mirrors the chevron pattern the
 * synced-balances list used before it moved in here. */
const CardSection = ({label, open, onToggle, children}: SectionProps) => (
  <div className="border-border border-t">
    <button
      onClick={onToggle}
      className="text-foreground hover:bg-muted/40 flex w-full cursor-pointer items-center gap-1.5 px-4 py-3 text-sm font-medium transition"
    >
      {open ? (
        <ChevronDown className="text-muted-foreground size-4" />
      ) : (
        <ChevronRight className="text-muted-foreground size-4" />
      )}
      {label}
    </button>
    {open && <div className="px-4 pb-4">{children}</div>}
  </div>
);

type Reading = BalanceReadingsQuery['balanceReadings'][number];

type ReadingRowProps = {
  reading: Reading;
  currency: string;
  isCreditCard: boolean;
  onPromote: (reading: Reading) => void;
  onDelete: (reading: Reading) => void;
};

/**
 * A synced Plaid reading. Readings are write-once telemetry: no detail page,
 * no editing — just Promote (record an anchor for the same date; the reading
 * is kept) and Delete. This card is their only home in the UI.
 */
const ReadingRow = ({
  reading,
  currency,
  isCreditCard,
  onPromote,
  onDelete,
}: ReadingRowProps) => {
  const amount = Money.fromServer({
    amount: toDisplayBalance(reading.balance, isCreditCard),
    currency,
  });

  return (
    <div className="border-border bg-card rounded-xl border px-4 py-3 shadow-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-foreground shrink-0 font-medium">
          {reading.date}
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <AmountDisplay amount={amount} variant="plain" />
          <Button
            variant="outline"
            onClick={() => onPromote(reading)}
            className="h-7 px-2 text-xs"
          >
            Promote
          </Button>
          <button
            onClick={() => onDelete(reading)}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg p-1.5 transition"
            title="Delete synced balance"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

type PlaidCardProps = {
  account: DepositoryAccount | InvestmentAccount;
  /**
   * Called after a reading is promoted to an anchor or deleted, so the host
   * overview can refetch derived data (balance history, Account.balance).
   */
  onReadingChanged?: () => void;
};

/**
 * The "Plaid" card on the account Overview tab, shown only for Plaid-linked
 * accounts. Its header is built entirely from the shell's account query — no
 * query fires on mount. Two collapsed sections lazily fetch their data the
 * first time they are expanded: the synced daily balances (with promote /
 * delete) and the raw Plaid account JSON from the last sync.
 *
 * Serves both depository and investment accounts; the union only matters for
 * the credit-card sign convention applied to reading amounts.
 */
export const PlaidCard = ({account, onReadingChanged}: PlaidCardProps) => {
  const plaid = account.plaid;
  const {recordBalanceAnchorModal, deleteBalanceEntryModal} = useModals();

  const [showSynced, setShowSynced] = React.useState(false);
  const [showRaw, setShowRaw] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const isCreditCard =
    account.__typename === 'DepositoryAccount' &&
    account.depositoryType === DepositoryType.CreditCard;

  // Both sections stay skipped until opened, so the card adds nothing to the
  // overview's initial request.
  const {data: readingData, refetch: refetchReadings, loading: readingsLoading} =
    useBalanceReadingsQuery({
      variables: {accountId: account._id, limit: 200},
      fetchPolicy: 'cache-and-network',
      skip: !showSynced,
    });
  const {
    data: jsonData,
    loading: jsonLoading,
    error: jsonError,
    refetch: refetchJson,
  } = useAccountPlaidJsonQuery({
    variables: {id: account._id},
    skip: !showRaw,
  });

  const readings = readingData?.balanceReadings ?? [];

  const plaidJson = jsonData?.account?.plaid?.plaidJson ?? null;
  const parsed = React.useMemo((): JsonValue | null => {
    if (!plaidJson) {
      return null;
    }
    try {
      const value: JsonValue = JSON.parse(plaidJson);
      return value;
    } catch {
      return null;
    }
  }, [plaidJson]);

  if (!plaid) {
    return null;
  }

  const handlePromote = (reading: Reading) => {
    // Promoting a synced Plaid reading records an anchor on the same date as a
    // separate row — the reading is kept untouched. The balance is prefilled
    // but editable (credit-card statement balances differ from Plaid's
    // pending-inclusive snapshot).
    recordBalanceAnchorModal.openModal({
      accountId: account._id,
      currency: account.currency,
      isCreditCard,
      initialDate: DateTime.fromISO(reading.date),
      initialBalance: Money.fromServer({
        amount: toDisplayBalance(reading.balance, isCreditCard),
        currency: account.currency,
      }),
      onSuccess: () => onReadingChanged?.(),
    });
  };

  const handleDelete = (reading: Reading) => {
    deleteBalanceEntryModal.openModal({
      kind: 'reading',
      entryId: reading._id,
      entryDate: reading.date,
      onDeleted: () => {
        void refetchReadings();
        onReadingChanged?.();
      },
    });
  };

  const handleCopy = async () => {
    if (parsed == null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(parsed, null, 2));
    } catch {
      // Clipboard unavailable (e.g. permission denied) — don't claim success.
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const obj = asObject(parsed);
  const balances = asObject(obj?.balances);
  const mask = getString(obj, 'mask');
  const officialName =
    getString(obj, 'official_name') ?? getString(obj, 'name');
  const type = getString(obj, 'type');
  const subtype = getString(obj, 'subtype');
  const currency = getString(balances, 'iso_currency_code') ?? 'USD';

  return (
    <div className="border-border bg-card rounded-xl border shadow-xs">
      {/* Header: identity from the shell's account query. The right side is
          left open for a future Disconnect action. */}
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-foreground m-0 text-sm font-medium">Plaid</h2>
          <PlaidConnectionStatus
            accountId={account._id}
            institutionName={plaid.institutionName}
            className="mt-0.5 text-xs"
          />
        </div>
      </div>

      {/* Synced balances: daily Plaid readings. They feed the balance history
          chart above, so the list stays collapsed — expanding it is only
          needed to promote a reading into a reconcilable anchor or to delete a
          bad one, actions that exist nowhere else. */}
      <CardSection
        label="Synced balances"
        open={showSynced}
        onToggle={() => setShowSynced((prev) => !prev)}
      >
        {readingsLoading && readings.length === 0 ? (
          <p className="text-muted-foreground m-0 text-sm">Loading…</p>
        ) : readings.length === 0 ? (
          <p className="text-muted-foreground m-0 text-sm">
            No synced balances yet — they appear after the next sync.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {readings.map((reading) => (
              <ReadingRow
                key={reading._id}
                reading={reading}
                currency={account.currency}
                isCreditCard={isCreditCard}
                onPromote={handlePromote}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </CardSection>

      {/* Raw Plaid data: the account object from the most recent sync, verbatim. */}
      <CardSection
        label="Raw Plaid data"
        open={showRaw}
        onToggle={() => setShowRaw((prev) => !prev)}
      >
        {jsonLoading && !jsonData ? (
          <p className="text-muted-foreground m-0 text-sm">Loading…</p>
        ) : jsonError && !jsonData ? (
          <div className="flex flex-col items-start gap-2">
            <span className="text-muted-foreground text-sm">
              Failed to load Plaid data
            </span>
            <Button variant="outline" onClick={() => void refetchJson()}>
              Retry
            </Button>
          </div>
        ) : parsed == null ? (
          <p className="text-muted-foreground m-0 text-sm">
            No raw Plaid data stored yet — it appears after the next sync.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Summary strip: the fields worth reading without digging in the
                JSON. */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <SummaryItem
                label="Account number"
                value={mask ? `•••• ${mask}` : null}
              />
              <SummaryItem label="Official name" value={officialName} />
              <SummaryItem
                label="Type"
                value={
                  type ? [type, subtype].filter(Boolean).join(' / ') : null
                }
              />
              <SummaryItem
                label="Available"
                value={formatDollars(getNumber(balances, 'available'), currency)}
              />
              <SummaryItem
                label="Current"
                value={formatDollars(getNumber(balances, 'current'), currency)}
              />
              <SummaryItem
                label="Limit"
                value={formatDollars(getNumber(balances, 'limit'), currency)}
              />
            </div>

            <div className="border-border rounded-xl border">
              <div className="border-border flex items-center justify-between gap-4 border-b px-4 py-3">
                <p className="text-muted-foreground m-0 text-xs">
                  The account object from the most recent Plaid sync, verbatim.
                </p>
                <Button
                  variant="outline"
                  onClick={() => void handleCopy()}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="size-4" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                  {copied ? 'Copied' : 'Copy JSON'}
                </Button>
              </div>
              <div className="p-4">
                <JsonTree value={parsed} />
              </div>
            </div>
          </div>
        )}
      </CardSection>
    </div>
  );
};
